import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { containerService } from '../../services/containerService';
import { getStatusLabel } from '../../services/containerService';
import { Package, Ship, Calendar, MapPin, Download, ArrowLeft, Edit2, X } from 'lucide-react';

export default function ContainerDetailManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerId = searchParams?.get('id');

  const [container, setContainer] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  // ✅ KPI Total colis — SUM(shipment_items.quantity) via shipments déjà chargés
  const totalPackages = (shipments || [])?.reduce(
    (sum, shipment) => sum + (Number(shipment?.totalPackages) || 0),
    0
  );

  console.log('CONTAINER SHIPMENTS', shipments);
  console.log('TOTAL PACKAGES KPI', totalPackages);

  useEffect(() => {
    if (containerId) {
      loadContainerDetails();
    }
  }, [containerId]);

  const loadContainerDetails = async () => {
    try {
      setLoading(true);
      const [containerData, shipmentsData] = await Promise.all([
        containerService?.getById(containerId),
        containerService?.getContainerShipments(containerId)
      ]);
      setContainer(containerData);
      setShipments(shipmentsData);
      setError('');
    } catch (err) {
      setError(err?.message);
      console.error('Error loading container details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportManifest = async () => {
    try {
      setExporting(true);
      await containerService?.exportManifest(containerId);
      setError('');
    } catch (err) {
      setError(`Erreur lors de l'export: ${err?.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleCloseContainer = async () => {
    try {
      await containerService?.updateStatus(containerId, 'closed');
      await loadContainerDetails();
    } catch (err) {
      setError(`Erreur: ${err?.message}`);
    }
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = "px-3 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'draft':
        return `${baseClass} bg-gray-100 text-gray-700`;
      case 'open':
        return `${baseClass} bg-blue-100 text-blue-700`;
      case 'closed':
        return `${baseClass} bg-yellow-100 text-yellow-700`;
      case 'departed':
        return `${baseClass} bg-purple-100 text-purple-700`;
      case 'arrived':
        return `${baseClass} bg-green-100 text-green-700`;
      default:
        return `${baseClass} bg-gray-100 text-gray-700`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Update the container details display
  const statusLabel = container?.statusLabel || getStatusLabel(container?.status) || 'Statut inconnu';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails...</p>
        </div>
      </div>
    );
  }

  if (!container) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Conteneur non trouvé</p>
          <button
            onClick={() => navigate('/container-management-dashboard')}
            className="mt-4 text-green-600 hover:text-green-700 font-medium"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  console.log('TOTAL PACKAGES KPI FINAL:', totalPackages);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/container-management-dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                {container?.containerCode}
              </h1>
              <p className="text-sm text-gray-500">Détails du conteneur maritime</p>
            </div>
            <span className={getStatusBadgeClass(container?.status)}>
              {container?.status}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleExportManifest}
              disabled={exporting}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Export en cours...' : 'Exporter le Manifeste (CSV)'}
            </button>

            {container?.status === 'open' && (
              <button
                onClick={handleCloseContainer}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <X className="w-4 h-4 mr-2" />
                Fermer le Conteneur
              </button>
            )}

            <button
              onClick={() => setEditMode(!editMode)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Modifier les Infos
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Container Information Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations du Conteneur</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Statut</p>
              <p className="font-semibold text-gray-900">{statusLabel}</p>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Port de départ</p>
                <p className="text-base font-semibold text-gray-900">
                  {container?.departurePort || 'Non spécifié'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Port de destination</p>
                <p className="text-base font-semibold text-gray-900">
                  {container?.destinationPort || 'Non spécifié'}
                </p>
              </div>
            </div>

            {/* Dates */}
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-purple-600 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Date de départ</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatDate(container?.departureDate)}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-orange-600 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Date d'arrivée estimée</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatDate(container?.etaDate)}
                </p>
              </div>
            </div>

            {/* Shipping Information */}
            <div className="flex items-start gap-3">
              <Ship className="w-5 h-5 text-indigo-600 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Compagnie maritime</p>
                <p className="text-base font-semibold text-gray-900">
                  {container?.shippingLine || 'Non spécifié'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Ship className="w-5 h-5 text-cyan-600 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Nom du navire</p>
                <p className="text-base font-semibold text-gray-900">
                  {container?.vesselName || 'Non spécifié'}
                </p>
              </div>
            </div>

            {/* Statistics — KPIs calculés depuis shipment_items.quantity */}
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Total envois</p>
                <p className="text-base font-semibold text-gray-900">
                  {/* COUNT DISTINCT shipments affectés au conteneur */}
                  {shipments?.length || 0}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-500">Total colis</p>
                <p className="text-base font-semibold text-gray-900">
                  {/* SUM(shipment_items.quantity) — source de vérité */}
                  {totalPackages}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {container?.notes && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-500 mb-2">Notes</p>
              <p className="text-sm text-gray-700">{container?.notes}</p>
            </div>
          )}
        </div>

        {/* Shipments List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Envois dans ce Conteneur ({shipments?.length})
            </h2>
          </div>

          {shipments?.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun envoi assigné à ce conteneur</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Référence
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expéditeur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ville Départ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Destinataire
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ville Arrivée
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {shipments?.map((shipment) => (
                    <tr key={shipment?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/shipment-detail-management?id=${shipment?.id}`)}
                          className="text-sm font-medium text-green-600 hover:text-green-700"
                        >
                          {shipment?.reference}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{shipment?.senderName}</div>
                        <div className="text-sm text-gray-500">{shipment?.senderPhone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{shipment?.senderCity}</div>
                        <div className="text-sm text-gray-500">{shipment?.senderCountry}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{shipment?.receiverName}</div>
                        <div className="text-sm text-gray-500">{shipment?.receiverPhone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{shipment?.receiverCity}</div>
                        <div className="text-sm text-gray-500">{shipment?.receiverCountry}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {shipment?.currentStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Edit Modal Placeholder */}
      {editMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Modifier le Conteneur</h3>
            <p className="text-gray-600 mb-4">
              Fonctionnalité à implémenter : formulaire de modification
            </p>
            <button
              onClick={() => setEditMode(false)}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}